import { computed, ref, watch, type Ref } from 'vue'
import type { ColdMonitoringResponseCall } from 'shared'
import { FetchApiError } from '@/utils/fetchApi'
import { useDebounceLock } from '@/composables/useDebounceLock'
import { hydrateContextSnapshot, useLivekitContext } from '@/composables/livekit'
import { useColdCallDetailsQuery } from '@/composables/queries/cold-monitoring/useColdCallDetailsQuery'
import type { OAudioController, useDateTime } from '@ai/ui-components'

export type ColdCallRow = Omit<ColdMonitoringResponseCall['callInfo'], 'agent'> & {
  agent?: ColdMonitoringResponseCall['callInfo']['agent']
  durationSec: number
}

interface ColdCallModalControllerDeps {
  calls: Ref<ColdCallRow[]>
  initialItem: Ref<ColdCallRow>
  formatTimerDuration: ReturnType<typeof useDateTime>['formatTimerDuration']
  audioController?: Pick<OAudioController, 'stop'>
}

const useColdMonitoringCallDetails = (
  callId: Ref<string | null | undefined>,
  callDate: Ref<string | null>,
) => {
  const safeCallId = computed(() => callId.value ?? null)

  const livekitContext = useLivekitContext()

  const query = useColdCallDetailsQuery(safeCallId, callDate)

  const audio = computed(() => query.data.value?.audio ?? null)
  const summary = computed(() => query.data.value?.summary ?? null)
  const conversationOutput = computed(() => query.data.value?.conversationOutput ?? null)

  watch(
    () => query.data.value?.context ?? null,
    (ctx) => {
      livekitContext.resetContext()
      if (!ctx) return
      hydrateContextSnapshot(livekitContext, ctx)
    },
    { immediate: true },
  )

  const transcriptFromContext = computed(() => livekitContext.transcripts.value)
  const eventsFromContext = computed(() => livekitContext.tools.value)

  const isAccessDenied = computed(
    () =>
      query.error.value instanceof FetchApiError &&
      [403, 404, 422].includes(query.error.value.status),
  )

  return {
    transcript: transcriptFromContext,
    events: eventsFromContext,
    audio,
    summary,
    conversationOutput,
    isLoading: query.isLoading,
    isAccessDenied,
  }
}

export const useColdCallModalController = ({
  calls,
  initialItem,
  formatTimerDuration,
  audioController,
}: ColdCallModalControllerDeps) => {
  const panelOpen = ref(false)
  const currentCallIndex = ref(0)
  const callDetailsCallId = ref<string | null>(null)
  const callDetailsDate = ref<string | null>(null)
  // Set when opening a call that is not in the current page (e.g. direct link to page 3+ call)
  const directLinkCallId = ref<string | null>(null)
  const { withDebounce, isDebouncing } = useDebounceLock(500)

  const details = useColdMonitoringCallDetails(callDetailsCallId, callDetailsDate)

  const currentCall = computed<ColdCallRow>(() => {
    if (directLinkCallId.value) {
      return {
        id: directLinkCallId.value,
        callerNumber: '',
        conversationOutput: null,
        summary: null,
        language: 'fr',
        startTime: 0,
        endTime: 0,
        agent: { id: '', name: '' },
        durationSec: 0,
      }
    }
    return calls.value[currentCallIndex.value] ?? initialItem.value
  })
  const hasPrev = computed(() => !directLinkCallId.value && currentCallIndex.value > 0)
  const hasNext = computed(
    () => !directLinkCallId.value && currentCallIndex.value < calls.value.length - 1,
  )

  const openPanel = (callId: string) => {
    const previousCallId = panelOpen.value ? currentCall.value.id : null
    const idx = calls.value.findIndex((c) => c.id === callId)
    if (idx >= 0) {
      directLinkCallId.value = null
      currentCallIndex.value = idx
      const startTime = calls.value[idx]?.startTime
      callDetailsDate.value = startTime ? new Date(startTime).toISOString().split('T')[0] : null
    } else {
      directLinkCallId.value = callId
      callDetailsDate.value = null
    }
    panelOpen.value = true
    callDetailsCallId.value = callId
    if (previousCallId && previousCallId !== callId) {
      audioController?.stop()
    }
  }

  const nextCall = () => {
    void withDebounce(() => {
      if (details.isLoading.value) return
      if (hasNext.value) {
        const nextCallId = calls.value[currentCallIndex.value + 1]?.id
        if (nextCallId) openPanel(nextCallId)
      }
    })
  }

  const prevCall = () => {
    void withDebounce(() => {
      if (details.isLoading.value) return
      if (hasPrev.value) {
        const prevCallId = calls.value[currentCallIndex.value - 1]?.id
        if (prevCallId) openPanel(prevCallId)
      }
    })
  }

  watch(details.isAccessDenied, (denied) => {
    if (denied) {
      panelOpen.value = false
    }
  })

  watch(panelOpen, (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) {
      directLinkCallId.value = null
      audioController?.stop()
    }
  })

  const callDuration = computed(() =>
    formatTimerDuration({ value: currentCall.value.durationSec, alwaysIncludeHours: true }),
  )
  const callIdFormatted = computed(() => currentCall.value.id)

  return {
    ...details,
    panelOpen,
    isDebouncing,
    currentCall,
    hasPrev,
    hasNext,
    nextCall,
    prevCall,
    openPanel,
    callDuration,
    callIdFormatted,
  }
}
